// WebKit can fail an IndexedDB request for reasons that have nothing to do
// with the data and clear up by themselves:
//   - iOS Safari drops its IndexedDB connection after a tab sits idle or
//     backgrounded ("Connection to Indexed Database server lost").
//   - iOS freezes the WebContent process when the app is backgrounded; a
//     request in flight at that instant finds its transaction already torn
//     down when it runs ("Attempt to get records from database without an
//     in-progress transaction"). Reproduced on an iPad: failures land only
//     within ~1.5 s of a background/foreground transition, and the next
//     request succeeds. WebKit's SQLiteIDBBackingStore emits one such message
//     per operation; only the READ variant is matched, because it is the one
//     observed and the one the callers issue (a single Dexie toArray with no
//     app-owned transaction lifetime). The write variants are left unmatched
//     on purpose: WebKit emits the same tail for a genuine misuse (a request
//     issued after its transaction committed), and an unmatched message
//     still reports, so a new variant shows up instead of being hidden.
// Dexie may surface either as the raw browser UnknownError or wrapped in
// DatabaseClosedError.inner, so inspect a few wrapper layers while still
// matching only the known messages.
const TRANSIENT_MESSAGES = [
  "connection to indexed database server lost",
  "attempt to get records from database without an in-progress transaction",
];

export function isTransientIndexedDbDisconnectError(error: unknown): boolean {
  const parts: string[] = [];
  let current: unknown = error;

  for (let depth = 0; depth < 4 && current != null; depth++) {
    if (current instanceof Error) {
      parts.push(current.name, current.message);
      current =
        (current as Error & { cause?: unknown; inner?: unknown }).inner ??
        current.cause;
      continue;
    }

    if (typeof current === "object") {
      const maybeError = current as { name?: unknown; message?: unknown };

      if (typeof maybeError.name === "string") {
        parts.push(maybeError.name);
      }

      if (typeof maybeError.message === "string") {
        parts.push(maybeError.message);
      }

      break;
    }

    if (typeof current === "string") {
      parts.push(current);
    }

    break;
  }

  const joined = parts.join(" ").toLowerCase();
  return TRANSIENT_MESSAGES.some((message) => joined.includes(message));
}
